import { styled } from '@mui/material/styles';
import type { PickerDayProps } from '@mui/x-date-pickers/PickerDay';
import { PickerDay } from '@mui/x-date-pickers/PickerDay';
import { isSameDay } from 'date-fns';
import { createContext, useContext } from 'react';
import { intent, radius, spacingTokens } from '../../theme/tokens/index.ts';
import { isAfterDay, isBeforeDay } from './dayComparison.ts';

/**
 * The range being picked, shared with every day cell.
 *
 * Passed by context rather than through `slotProps.day` on purpose: `slots.day` is typed
 * `ElementType<PickerDayProps>`, so extra props would need a cast at every call site, and
 * building the slot component inline (to close over the state) would remount all 42 cells on
 * every hover.
 */
export interface FilterRangeContextValue {
  /** First edge picked. */
  start: Date | null;
  /** Second edge — `null` while the range is half-picked. */
  end: Date | null;
  /** Hovered / focused day, previewing where the range would end. */
  preview: Date | null;
  /** A day was clicked or activated by keyboard. */
  onDayPick: (day: Date) => void;
  /** Hover or focus moved onto a day. */
  onDayPreview: (day: Date) => void;
}

const FilterRangeContext = createContext<FilterRangeContextValue | null>(null);

export const FilterRangeProvider = FilterRangeContext.Provider;

// The band that joins the days of a range into one continuous strip.
//
// Each wrapper is exactly one grid column wide: MUI's day is 36px with a 2px margin either side
// (DAY_SIZE / DAY_MARGIN), so `inset: 0` already spans the gutters and neighbouring bands touch —
// no negative-margin bleed needed. The gutters are NOT removed (MUI's weekday header row hard-codes
// the same 36+2+2 columns, so zeroing the day margins would misalign the grid by 4px per column).
//
// Paint order: an absolutely-positioned `::before` would otherwise cover a static button sibling,
// so both get an explicit z-index.
const StyledRangeDayCell = styled('div')({
  position: 'relative',
  display: 'inline-flex',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: 0,
    backgroundColor: intent.primary.subtle,
  },
  '& .MuiPickerDay-root': {
    position: 'relative',
    zIndex: 1,
  },
  // Not in the range — no band at all.
  '&:not([data-in-range="true"])::before': {
    content: 'none',
  },
  // At an edge the band stops where the day's pill starts, and takes the same pill radius, so the
  // strip ends flush with the round cell instead of overshooting it by the gutter.
  '&[data-edge~="start"]::before': {
    left: spacingTokens.sp2,
    borderTopLeftRadius: radius.pill,
    borderBottomLeftRadius: radius.pill,
  },
  '&[data-edge~="end"]::before': {
    right: spacingTokens.sp2,
    borderTopRightRadius: radius.pill,
    borderBottomRightRadius: radius.pill,
  },
});

/**
 * FilterRangeDay — the `slots.day` used by `FilterDateButton` in `mode="range"`.
 *
 * Wraps the free (Community) `PickerDay` in a band that paints the selected interval, which is
 * what makes a range readable on a single-value `DateCalendar`. **This is the free replacement for
 * MUI X Pro's `DateRangeCalendar`** — see Filter.usage.md / DECISIONS for why Pro is off the table.
 *
 * The two edges are rendered with the normal `selected` day treatment (subtle fill + primary ring,
 * per Figma 1922:14602), so a range's ends look exactly like a single selected date — no separate
 * design vocabulary, no new tokens.
 *
 * Selection is intercepted at `onDaySelect` rather than read from `DateCalendar`'s `onChange`, so
 * re-clicking the day that is already the calendar's `value` still registers (that is how a
 * single-day range gets picked). Hover *and* focus drive the preview, so keyboard users see the
 * same feedback as pointer users.
 */
export function FilterRangeDay(props: PickerDayProps) {
  const { day, outsideCurrentMonth, onMouseEnter, onFocus, ...rest } = props;
  const ctx = useContext(FilterRangeContext);

  // Rendered outside a range calendar (or on a filler cell) — behave like a plain day.
  if (!ctx || outsideCurrentMonth) {
    return <PickerDay {...rest} day={day} outsideCurrentMonth={outsideCurrentMonth} />;
  }

  const { start, end, preview, onDayPick, onDayPreview } = ctx;

  // While half-picked, the hovered/focused day stands in for the missing edge. Either edge may be
  // the earlier one (you are allowed to pick backwards), so order them before comparing.
  const provisionalEnd = end ?? (start ? preview : null);
  const [from, to] =
    start && provisionalEnd && isBeforeDay(provisionalEnd, start) ? [provisionalEnd, start] : [start, provisionalEnd];

  const isFrom = Boolean(from && isSameDay(day, from));
  const isTo = Boolean(to && isSameDay(day, to));
  const isEdge = isFrom || isTo;
  const within = from != null && to != null && !isBeforeDay(day, from) && !isAfterDay(day, to);

  // A one-day range needs no band — the selected cell already says everything.
  const inRange = within && !(isFrom && isTo);
  const edge = [isFrom && 'start', isTo && 'end'].filter(Boolean).join(' ');

  // What gets ANNOUNCED follows the real pick, never the preview: moving the keyboard cursor
  // across the grid must not tell a screen reader that those days are selected. While the range
  // is half-picked `end` is null, so only the first edge announces.
  const [realFrom, realTo] = start && end && isBeforeDay(end, start) ? [end, start] : [start, end];
  const ariaSelected = Boolean(
    realFrom && (isSameDay(day, realFrom) || (realTo && !isBeforeDay(day, realFrom) && !isAfterDay(day, realTo)))
  );

  return (
    <StyledRangeDayCell data-in-range={inRange ? 'true' : undefined} data-edge={edge || undefined}>
      <PickerDay
        {...rest}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        // Only the edges read as "selected"; the days between are carried by the band.
        selected={isEdge}
        // DayCalendar derives `aria-selected` from its own single `value`, which knows nothing
        // about the second edge — restate it for the whole picked interval.
        aria-selected={ariaSelected}
        onDaySelect={onDayPick}
        onMouseEnter={(event, hovered) => {
          onDayPreview(hovered);
          onMouseEnter?.(event, hovered);
        }}
        onFocus={(event, focused) => {
          onDayPreview(focused);
          onFocus?.(event, focused);
        }}
      />
    </StyledRangeDayCell>
  );
}
